import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import moment from 'moment'

import { STEPS_ORDER_BOOK } from '../../../../utils/constants'
import routes from '../../../../routers'
import { getUserGroups } from '../../../../api/group'
import { getCookbooks, saveCookbook } from '../../../../api/cookbook'
import CustomTitle from '../CustomTitle'
import Footer from '../Footer'

import styles from './FirstStep.module.css'

const FirstStep = () => {
  const [userGroups, setUserGroups] = useState(null)
  const [drafts, setDrafts] = useState([])
  const [group, setGroup] = useState('')
  const [showError, setShowError] = useState(false)
  const [saving, setSaving] = useState(false)
  const [showAllDrafts, setShowAllDrafts] = useState(false)
  const navigate = useNavigate()

  // Picking a group starts a saved draft, so nothing is lost if you leave
  const handleSubmit = (e) => {
    e.preventDefault()
    if (!group) return setShowError(true)
    setSaving(true)
    saveCookbook({ group_id: group })
      .then(({ data }) => {
        navigate('.', {
          state: { currentStep: STEPS_ORDER_BOOK['SECOND_STEP'], groupId: group, orderId: data.id },
        })
      })
      .catch(() => setSaving(false))
  }

  // A requested book opens on its order details; any other draft on its recipes
  const continueDraft = ({ id, group_id, order_request }) => {
    navigate('.', {
      state: {
        currentStep: order_request
          ? STEPS_ORDER_BOOK['CHECKOUT_STEP']
          : STEPS_ORDER_BOOK['SECOND_STEP'],
        groupId: group_id,
        orderId: id,
      },
    })
  }

  useEffect(() => {
    getUserGroups()
      .then(({ data }) => {
        setUserGroups(data || [])
        if (data?.length === 1) setGroup(data[0].id)
      })
      .catch(() => setUserGroups([]))
    getCookbooks()
      .then(({ data }) => setDrafts(data || []))
      .catch(() => setDrafts([]))
  }, [])

  const groupName = (id) => (userGroups || []).find((g) => g.id === id)?.name || ''
  const draftDetail = (draft) =>
    [groupName(draft.group_id), `started ${moment(draft.created_at).format('MMM D')}`]
      .filter(Boolean)
      .join(', ')

  return (
    <form className="cbStep" onSubmit={handleSubmit}>
      {drafts.length ? (
        <section className="cbSection cbSectionSoft">
          <CustomTitle
            title="Pick up where you left off"
            hoverText="Your cookbooks are saved as you go."
          />
          <div className={styles.drafts}>
            {(showAllDrafts ? drafts : drafts.slice(0, 3)).map((draft) => (
              <button
                key={draft.id}
                type="button"
                className={styles.draft}
                onClick={() => continueDraft(draft)}
              >
                <span className={styles.draftText}>
                  <b>
                    {draft.title || 'Untitled cookbook'}
                    {draft.order_request ? (
                      <span className={styles.requested}>Requested</span>
                    ) : null}
                  </b>
                  <span>
                    {draft.order_request
                      ? 'Saved. We’ll email you to finish your order.'
                      : draftDetail(draft)}
                  </span>
                </span>
                <span className={styles.draftGo}>{draft.order_request ? 'View' : 'Continue'}</span>
              </button>
            ))}
          </div>
          {drafts.length > 3 && !showAllDrafts ? (
            <button type="button" className={styles.more} onClick={() => setShowAllDrafts(true)}>
              Show {drafts.length - 3} more
            </button>
          ) : null}
        </section>
      ) : null}

      <section className="cbSection">
        <CustomTitle
          title={
            drafts.length ? 'Or start a new cookbook' : 'Which group’s recipes go in the book?'
          }
          hoverText="A cookbook is made from one group’s recipes. You’ll choose exactly which ones next."
        />
        {userGroups && !userGroups.length ? (
          <p className="cbMuted">
            You’re not in a group yet.{' '}
            <Link className="cbTextLink" to={routes.createGroup}>
              Start a group
            </Link>{' '}
            and add a few recipes first.
          </p>
        ) : null}
        <div className={`cbChoices ${styles.groups}`} role="radiogroup">
          {(userGroups || []).map((g) => (
            <label key={g.id} className={`cbChoice ${group === g.id ? 'cbChoiceOn' : ''}`}>
              <input
                type="radio"
                name="group"
                value={g.id}
                checked={group === g.id}
                onChange={() => {
                  setGroup(g.id)
                  setShowError(false)
                }}
              />
              <span className="cbChoiceDot" />
              <span className="cbChoiceBody">
                <b>{g.name}</b>
              </span>
            </label>
          ))}
        </div>
      </section>

      <Footer
        backTo={null}
        disabled={saving}
        errorShow={showError}
        errorMessage="Pick a group first"
        buttonText={saving ? 'Starting…' : 'Next: choose recipes'}
      />
    </form>
  )
}

export default FirstStep
